import cx from 'classnames'
import { PropsWithChildren, useState, useRef, useEffect, useMemo } from 'react'
import { Modal, useModals, StackModalProps } from 'almanac-ui/Modal'
import { observer } from 'mobx-react-lite'

import Styles from './CreditsBalanceModal.module.scss'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { Button } from 'blaze-ui/Button'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { ReferralModalV2 } from 'components/Settings/ReferralModalV2/ReferralModalV2'
import { ChevronDown2, LinkExternal, AlertTriangle, ArrowRefresh2 } from 'blaze-ui/icons'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import dayjs from 'dayjs'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'
import { pluralize } from 'utils'
import { getPlanCredits } from 'components/Settings/Workspace/AutopilotPlans/utils'
import LockOpen3 from 'blaze-ui/icons/24/LockOpen3'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { AutoReloadModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/AutoReloadModal/AutoReloadModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const EducationRow = ({
  text,
  children,
  isOpen,
  onToggle,
}: PropsWithChildren<{
  text: string
  isOpen: boolean
  onToggle: () => void
}>) => {
  return (
    <div className={Styles.educationRow}>
      <div className={Styles.textAndChevron} onClick={onToggle}>
        <span className={cx(Styles.text, Styles.grow)}>{text}</span>
        <div className={cx(Styles.chevronWrapper, { [Styles.rotated]: isOpen })}>
          <ChevronDown2 />
        </div>
      </div>
      <div className={cx(Styles.accordionContent, { [Styles.open]: isOpen })}>
        <div className={Styles.accordionInner}>{children}</div>
      </div>
    </div>
  )
}

type Props = StackModalProps

export const CreditsBalanceModal = observer((_props: Props) => {
  const { isMobile } = useResponsiveDesign()
  const isAutoReloadCreditsEnabled = useFlipperFlag('auto_reload_credits')
  const { openModal, closeModal } = useModals()
  const { creditsRemaining, baseCreditsAvailable, usageCosts, endDate, creditStatus, canAutoReload } = useCreditUsage()
  const {
    tierUsageStore: { currentSubscription, autopilotAddOns },
  } = useRootStore()
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const workspace = useCurrentWorkspace()
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]
  const autoReloadIsOn = canAutoReload
  const autoReloadIsOff = !workspace?.auto_reload_credits
  const autoReloadIsPaused = workspace?.auto_reload_credits && !canAutoReload

  const isStarterOrFreePlan =
    currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotStarter ||
    currentSubscription?.plan === PaymentPlanKey.Free ||
    workspace?.is_ap_freemium

  const openPurchaseCreditsModal = () => {
    if (
      !isOnPioneer &&
      currentSubscription?.status === 'trialing' &&
      (variant === 'low_on_credits' || variant === 'no_credits')
    ) {
      openModal(AutopilotUpgradeEntryModal, { variant })
      return
    }

    if (!autopilotContentCreditsPrice) return

    closeModal()
    openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice })
  }

  const openReferralModal = () => {
    openModal(ReferralModalV2, { onAction: closeModal })
  }

  const toggleAccordion = (accordionKey: string) => {
    // Clear any existing timeout
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }

    // If clicking the same accordion, just toggle it
    if (openAccordion === accordionKey) {
      setOpenAccordion(null)
      return
    }

    // If no accordion is open, open the clicked one immediately
    if (!openAccordion) {
      setOpenAccordion(accordionKey)
      return
    }

    // If switching between accordions, close current one first
    setOpenAccordion(null)

    // Wait for close animation to complete, then open new one
    transitionTimeoutRef.current = setTimeout(() => {
      setOpenAccordion(accordionKey)
      transitionTimeoutRef.current = null
    }, 300) // Match the CSS transition duration
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  const pluralizedCredits = (cost: number) => {
    return `${cost} ${pluralize('credit', cost)}`
  }

  const isTrialing = currentSubscription?.status === 'trialing'
  const planKey =
    currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotGrowth
      ? PaymentPlanKey.BlazeAutopilotGrowth
      : PaymentPlanKey.BlazeAutopilotStarter

  // Map credit status from hook to modal variant format
  const variant = useMemo(() => {
    if (creditStatus === 'out') return 'no_credits'
    if (creditStatus === 'low') return 'low_on_credits'
    if (isAutoReloadCreditsEnabled && workspace?.auto_reload_credits && !canAutoReload) {
      return 'auto_reload_limit_reached'
    }
    return 'credit_balance'
  }, [creditStatus, canAutoReload, workspace?.auto_reload_credits, isAutoReloadCreditsEnabled])

  const planName = planKey === PaymentPlanKey.BlazeAutopilotGrowth ? 'Growth' : 'Starter'
  const ctaButtonLabel = currentSubscription?.status === 'active' ? 'Add More Credits' : `Upgrade to ${planName}`

  const modalHeader = useMemo(() => {
    if (variant === 'low_on_credits') {
      return "You're low on credits"
    }
    if (variant === 'no_credits') {
      return "You've used all your credits"
    }
    if (variant === 'auto_reload_limit_reached') {
      return "You've reached your auto-reload budget"
    }
    return ''
  }, [variant])

  const modalDescription = useMemo(() => {
    const credits = getPlanCredits(planKey)
    if (variant === 'low_on_credits' && isTrialing) {
      return (
        <>
          You've used most of your trial credits. Upgrade for <strong>{credits}</strong> fresh credits every month.
        </>
      )
    }
    if (variant === 'no_credits' && isTrialing) {
      return (
        <>
          You put your trial to work. Upgrade for <strong>{credits}</strong> fresh credits every month.
        </>
      )
    }
    return null
  }, [variant, isTrialing, planKey])

  const openUpgradeToGrowthModal = () => {
    openModal(AutopilotUpgradeEntryModal, {
      variant: 'more_credits',
      targetPlanKey: PaymentPlanKey.BlazeAutopilotGrowth,
    })
  }

  return (
    <Modal.Root bodyClassName={cx({ [Styles.modalContainer]: !isMobile })}>
      <Modal.Header title="Your credits balance" />
      <Modal.Content withoutFooter className={isMobile ? Styles.mobileContent : undefined}>
        <div className={Styles.content}>
          {variant !== 'credit_balance' && (
            <div className={Styles.header}>
              <Heading level={1}>{modalHeader}</Heading>
            </div>
          )}
          <div className={Styles.top}>
            <div className={Styles.ctaAndAutoReloadContainer}>
              <div className={cx(Styles.cta, Styles[variant])}>
                <div className={Styles.creditsCount}>
                  {variant === 'no_credits' ? (
                    <AlertTriangle color="var(--red-50)" size={30} />
                  ) : (
                    <Credits color="var(--purple-50)" size={30} />
                  )}
                  <Heading level={2}>{creditsRemaining} credits</Heading>
                </div>
                <div className={Styles.planInformationAndButton}>
                  {modalDescription ? (
                    <div className={Styles.planInformation}>
                      <span className={cx(Styles.text, Styles.messageText)}>{modalDescription}</span>
                    </div>
                  ) : (
                    <>
                      {!isOnPioneer && currentSubscription?.status === 'active' && (
                        <div className={Styles.planInformation}>
                          <span className={cx(Styles.text, Styles.messageText)}>
                            Your {currentSubscription?.plan ? nameMap[currentSubscription?.plan] : ''} plan includes{' '}
                            <strong>{baseCreditsAvailable}</strong> credits per month. Credits reset on{' '}
                            {dayjs(endDate).add(1, 'day').format('MMM D')}, and purchased credits expire after 30 days.
                          </span>
                        </div>
                      )}
                      {!isOnPioneer && currentSubscription?.status === 'trialing' && (
                        <div className={Styles.planInformation}>
                          <span className={cx(Styles.text, Styles.messageText)}>
                            Your {currentSubscription?.plan ? nameMap[currentSubscription?.plan] : ''} trial includes{' '}
                            <strong>{baseCreditsAvailable}</strong> credits on signup. Upgrade for fewer limits!
                          </span>
                        </div>
                      )}
                      {isOnPioneer && creditsRemaining === 0 && (
                        <span className={cx(Styles.text, Styles.messageText)}>
                          Your Pioneer plan requires credits to be purchased to access Stylized Images and AI Videos.
                        </span>
                      )}
                    </>
                  )}
                  <Button onPress={openPurchaseCreditsModal}>{ctaButtonLabel}</Button>
                </div>
              </div>
              {isAutoReloadCreditsEnabled && (
                <div
                  className={cx(Styles.autoReloadContainer, {
                    [Styles.on]: autoReloadIsOn,
                    [Styles.off]: autoReloadIsOff,
                    [Styles.paused]: autoReloadIsPaused,
                  })}
                >
                  <div className={Styles.iconTextAndPill}>
                    <ArrowRefresh2 />
                    <span className={Styles.text}>Auto-reload</span>
                    <div
                      className={cx(Styles.pill, {
                        [Styles.on]: autoReloadIsOn,
                        [Styles.off]: autoReloadIsOff,
                        [Styles.paused]: autoReloadIsPaused,
                      })}
                    >
                      {canAutoReload && 'ON'}
                      {workspace?.auto_reload_credits && !canAutoReload && 'PAUSED'}
                      {!workspace?.auto_reload_credits && 'OFF'}
                    </div>
                  </div>
                  <div className={Styles.manageButtonContainer}>
                    <span className={cx(Styles.text, Styles.budgetStatus)}>
                      {workspace?.auto_reload_credits &&
                        workspace?.auto_reload_limit &&
                        `$${workspace?.auto_reload_limit / 10} `}
                      {workspace?.auto_reload_credits && !workspace?.auto_reload_limit && 'Unlimited '}
                      {canAutoReload && 'monthly budget'}
                      {workspace?.auto_reload_credits && !canAutoReload && 'budget reached'}
                      {!workspace?.auto_reload_credits && 'Never run out of credits'}
                    </span>
                    <Button variant="secondary" size="lg" onPress={() => openModal(AutoReloadModal)}>
                      {workspace?.auto_reload_credits ? 'Manage' : 'Enable'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {isStarterOrFreePlan && (
              <div className={Styles.upgradeToGrowthContainer}>
                <Button frontIcon={LockOpen3} variant="tertiary" onPress={openUpgradeToGrowthModal}>
                  Upgrade to Growth to get 1200 credits per month
                </Button>
              </div>
            )}
            <span className={Styles.refer}>
              or{' '}
              <span className={Styles.underline} onClick={openReferralModal}>
                refer a friend
              </span>{' '}
              to get more credits
            </span>
          </div>
          <div className={Styles.bottom}>
            <div className={Styles.educationContainer}>
              <EducationRow
                text="What are credits?"
                isOpen={openAccordion === 'what-are-credits'}
                onToggle={() => toggleAccordion('what-are-credits')}
              >
                <div className={Styles.whatAreCreditsContainer}>
                  <span className={Styles.whatAreCredits}>
                    Blaze uses usage-based pricing in the form of credits to allow users to create various forms of
                    content in your workspace.
                  </span>
                  <span className={Styles.whatAreCredits}>
                    This allows Blaze to integrate with the best LLM and AI tools and gives you control over the quality
                    and quantity of content you want in your marketing.
                  </span>
                  <span className={Styles.whatAreCredits}>Purchased credits expire after 30 days</span>
                </div>
              </EducationRow>
              <EducationRow
                text="What uses credits?"
                isOpen={openAccordion === 'what-uses-credits'}
                onToggle={() => toggleAccordion('what-uses-credits')}
              >
                <div className={Styles.whatUsesCreditsContainer}>
                  {!isOnPioneer && (
                    <span className={Styles.creditCost}>
                      {pluralizedCredits(usageCosts?.content_credits_cost || 0)} per Brand Kit and Stock Content
                    </span>
                  )}
                  <span className={Styles.creditCost}>
                    {pluralizedCredits(usageCosts?.visual_styles_credits_cost || 0)} per Stylized Image
                  </span>
                  <span className={Styles.creditCost}>
                    {pluralizedCredits(usageCosts?.ai_image_credits_cost || 0)} per AI Image
                  </span>
                  <span className={Styles.creditCost}>
                    {pluralizedCredits(usageCosts?.video_credits_cost || 0)} per AI Video
                  </span>
                </div>
              </EducationRow>
              <EducationRow
                text="How can I earn more credits?"
                isOpen={openAccordion === 'how-to-earn-credits'}
                onToggle={() => toggleAccordion('how-to-earn-credits')}
              >
                <div className={Styles.learnMoreTextContainer}>
                  <span className={Styles.learnMoreText}>
                    You can earn 200 credits for every friend or peer that you refer to Blaze using your referral link.{' '}
                    <a
                      href="https://help.blaze.ai/en/articles/13017540-blaze-credits"
                      target="_blank"
                      rel="noreferrer"
                      className={Styles.underline}
                    >
                      Learn more about Blaze's referral program
                    </a>
                    .
                  </span>
                </div>
              </EducationRow>
            </div>
            <div className={Styles.bottomButtonContainer}>
              <ButtonLink
                to="https://help.blaze.ai/en/articles/13017540-blaze-credits"
                target="_blank"
                variant="editor"
                endIcon={LinkExternal}
              >
                Learn More About Credits
              </ButtonLink>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
})
